// ==========================================
// FILE: \src\screens\CatalogScreen.js
// ==========================================

import React, { useState } from 'react';
import { 
  View, Text, StyleSheet, TouchableOpacity, FlatList, 
  ActivityIndicator, Image, ScrollView, Platform 
} from 'react-native';
import { useWineContext } from '../context/WineContext'; 
import EnergyHeader from '../components/EnergyHeader'; 
import { 
  CheckCircle, History, Filter, WineOff, 
  Star, MapPin, PlusCircle, ChevronRight, ArrowLeft 
} from 'lucide-react-native';

export default function CatalogScreen({ navigation }) {
  // Estraiamo la funzione 't' per agganciare il dizionario multilingua centralizzato
  const { wines, isLoadingWines, t } = useWineContext();
  
  const [catalogTab, setCatalogTab] = useState('presente'); 
  const [filterType, setFilterType] = useState('Tutti');
  const [filterReady, setFilterReady] = useState(false);

  // Array dei filtri tipologici strutturati per mappare le chiavi stabili del DB
  const wineTypes = ['Tutti', 'Rosso', 'Bianco', 'Bollicine', 'Rosato', 'Dolce'];

  let displayedWines = wines.filter(wine => wine.status === catalogTab);
  
  if (catalogTab === 'presente') {
    if (filterType !== 'Tutti') {
      displayedWines = displayedWines.filter(w => w.type === filterType);
    }
    if (filterReady) {
      displayedWines = displayedWines.filter(w => w.isReadyToDrink === true);
    }
  }

  // Funzione helper anti-sfasamento per mappare la traduzione dei tipi a runtime senza rompere i filtri DB
  const getTypeLabel = (type) => {
    if (type === 'Tutti') return t('catalog.filters.all');
    const key = type.toLowerCase() === 'bollicine' ? 'bollicine' : type.toLowerCase() === 'rosato' ? 'rosato' : type.toLowerCase() === 'dolce' ? 'dolce' : type.toLowerCase() === 'bianco' ? 'bianco' : 'rosso';
    return t(`addWine.form.types.${key}`);
  };

  const renderWineCard = ({ item }) => (
    <TouchableOpacity 
      style={styles.card}
      onPress={() => navigation.navigate('Detail', { wine: item })}
    >
      {item.status === 'presente' && (
        <View style={styles.quantityBadge}>
          <Text style={styles.quantityBadgeText}>x{item.quantity}</Text>
        </View>
      )}

      <View style={styles.cardContent}>
        {item.image ? (
          <Image source={{ uri: item.image }} style={styles.wineImage} resizeMode="cover" />
        ) : (
          <View style={styles.placeholderImage}>
            <Text style={{fontSize: 30}}>🍷</Text>
          </View>
        )}

        <View style={styles.wineInfo}>
          <Text style={styles.wineName} numberOfLines={1}>{item.name}</Text>
          
          <Text style={styles.wineType}>
            {item.annata || 'S.A.'} • {getTypeLabel(item.type)}
          </Text>
          
          {item.status === 'passato' && item.rating > 0 && (
            <View style={styles.starsContainer}>
              {[1, 2, 3, 4, 5].map(s => (
                <Star key={s} size={14} color={item.rating >= s ? "#facc15" : "#e2e8f0"} fill={item.rating >= s ? "#facc15" : "transparent"} />
              ))}
            </View>
          )}

          {item.status === 'presente' && (
            <View style={styles.metaContainer}>
              {item.location ? (
                <View style={styles.metaItem}>
                  <MapPin size={12} color="#e11d48" />
                  <Text style={styles.metaText} numberOfLines={1}>{item.location}</Text>
                </View>
              ) : null}
              {item.drinkingWindow ? (
                <View style={styles.metaItem}>
                  <Text style={{fontSize: 10}}>⏳</Text>
                  <Text style={[styles.metaText, item.isReadyToDrink ? {color: '#16a34a'} : {color: '#f97316'}]} numberOfLines={1}>
                    {item.drinkingWindow}
                  </Text>
                </View>
              ) : null}
            </View>
          )}

          <Text style={styles.wineDescription} numberOfLines={2}>{item.description}</Text>
        </View>
        
        <ChevronRight color="#cbd5e1" size={24} style={{ alignSelf: 'center' }} />
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      
      <View style={styles.topRedBanner}>
         <EnergyHeader />
      </View>

      <View style={styles.pageHeaderCushion}>
        <View style={styles.headerLeft}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <ArrowLeft color="#0f172a" size={24} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('catalog.title')}</Text>
        </View>
        <TouchableOpacity onPress={() => navigation.navigate('AddWine')} style={styles.addButton}>
          <PlusCircle color="#fff" size={18} style={{marginRight: 6}} />
          <Text style={styles.addButtonText}>{t('catalog.buttons.add')}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.tabsContainer}>
        <TouchableOpacity 
          style={[styles.tab, catalogTab === 'presente' && styles.activeTab]}
          onPress={() => setCatalogTab('presente')}
        >
          <CheckCircle size={20} color={catalogTab === 'presente' ? '#e11d48' : '#64748b'} />
          <Text style={[styles.tabText, catalogTab === 'presente' && styles.activeTabText]}>{t('catalog.tabs.present')}</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={[styles.tab, catalogTab === 'passato' && styles.activeTab]}
          onPress={() => setCatalogTab('passato')}
        >
          <History size={20} color={catalogTab === 'passato' ? '#334155' : '#64748b'} />
          <Text style={[styles.tabText, catalogTab === 'passato' && {color: '#334155', fontWeight: 'bold'}]}>{t('catalog.tabs.past')}</Text>
        </TouchableOpacity>
      </View>

      {catalogTab === 'presente' && (
        <View style={styles.filtersWrapper}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtersScroll}>
            <TouchableOpacity 
              style={[styles.filterChip, filterReady ? styles.filterReadyActive : styles.filterReadyInactive]}
              onPress={() => setFilterReady(!filterReady)}
            >
              <Text style={filterReady ? styles.filterReadyTextActive : styles.filterReadyTextInactive}>
                {t('catalog.filters.readyToDrink')}
              </Text>
            </TouchableOpacity>

            {wineTypes.map(type => (
              <TouchableOpacity 
                key={type}
                style={[styles.filterChip, filterType === type ? styles.filterTypeActive : styles.filterTypeInactive]}
                onPress={() => setFilterType(type)}
              >
                <Text style={filterType === type ? styles.filterTypeTextActive : styles.filterTypeTextInactive}>
                  {getTypeLabel(type)}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      {isLoadingWines ? (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color="#e11d48" />
          <Text style={styles.emptyText}>{t('catalog.loading')}</Text>
        </View>
      ) : displayedWines.length === 0 ? (
        <View style={styles.centerContainer}>
          <View style={styles.emptyIconCircle}>
            <WineOff color="#cbd5e1" size={40} />
          </View>
          <Text style={styles.emptyTitle}>{t('catalog.empty.title')}</Text>
          <Text style={styles.emptyText}>{t('catalog.empty.text')}</Text>
        </View>
      ) : (
        <FlatList
          data={displayedWines}
          keyExtractor={item => item.id}
          renderItem={renderWineCard}
          contentContainerStyle={styles.listContainer}
          showsVerticalScrollIndicator={false}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  topRedBanner: {
    backgroundColor: '#e11d48',
    paddingBottom: 16,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    elevation: 5,
    boxShadow: '0px 4px 6px rgba(225, 29, 72, 0.3)', 
    zIndex: 50,
  },
  pageHeaderCushion: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginTop: 16, 
    padding: 16,
    borderRadius: 20,
    elevation: 3,
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.05)', 
    zIndex: 10,
  },
  headerLeft: { flexDirection: 'row', alignItems: 'center', flex: 1, paddingRight: 8 },
  backButton: { marginRight: 10, padding: 6, backgroundColor: '#f1f5f9', borderRadius: 10 },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', flex: 1 },
  addButton: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#e11d48', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12 },
  addButtonText: { color: 'white', fontWeight: 'bold', fontSize: 14 },
  tabsContainer: { flexDirection: 'row', paddingHorizontal: 20, marginTop: 10, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  tab: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, marginRight: 24, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  activeTab: { borderBottomColor: '#e11d48' },
  tabText: { fontSize: 15, fontWeight: '500', color: '#64748b', marginLeft: 8 },
  activeTabText: { color: '#e11d48', fontWeight: 'bold' },
  filtersWrapper: { paddingVertical: 12 },
  filtersScroll: { paddingHorizontal: 16 },
  
  // Stili modificati anti-sfasamento: via i width, spazio interno flessibile per stringhe lunghe
  filterChip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, marginRight: 8, borderWidth: 1, justifyContent: 'center', alignItems: 'center' },
  filterReadyInactive: { backgroundColor: '#ffffff', borderColor: '#e2e8f0' },
  filterReadyActive: { backgroundColor: '#dcfce7', borderColor: '#86efac' },
  filterReadyTextInactive: { color: '#64748b', fontWeight: '500', fontSize: 13 },
  filterReadyTextActive: { color: '#166534', fontWeight: 'bold', fontSize: 13 },
  filterTypeInactive: { backgroundColor: '#ffffff', borderColor: '#e2e8f0' },
  filterTypeActive: { backgroundColor: '#0f172a', borderColor: '#0f172a' },
  filterTypeTextInactive: { color: '#64748b', fontWeight: '500', fontSize: 13 },
  filterTypeTextActive: { color: 'white', fontWeight: 'bold', fontSize: 13 },
  listContainer: { padding: 16, paddingBottom: 40 },
  card: { backgroundColor: 'white', borderRadius: 20, padding: 16, marginBottom: 16, boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)', elevation: 2, position: 'relative' },
  cardContent: { flexDirection: 'row' },
  quantityBadge: { position: 'absolute', top: -10, right: -10, backgroundColor: '#e11d48', width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', zIndex: 10, boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)', elevation: 4 },
  quantityBadgeText: { color: 'white', fontWeight: 'bold', fontSize: 14 },
  wineImage: { width: 70, height: 100, borderRadius: 12, backgroundColor: '#f1f5f9' },
  placeholderImage: { width: 70, height: 100, borderRadius: 12, backgroundColor: '#f1f5f9', alignItems: 'center', justifyContent: 'center' },
  wineInfo: { flex: 1, marginLeft: 16, justifyContent: 'center' },
  wineName: { fontSize: 18, fontWeight: 'bold', color: '#0f172a', marginBottom: 4 },
  wineType: { fontSize: 14, color: '#64748b', marginBottom: 8, fontWeight: '500' },
  starsContainer: { flexDirection: 'row', marginBottom: 8 },
  metaContainer: { marginBottom: 8, gap: 4 },
  metaItem: { flexDirection: 'row', alignItems: 'center' },
  metaText: { fontSize: 12, color: '#e11d48', fontWeight: '500', marginLeft: 4 },
  wineDescription: { fontSize: 13, color: '#94a3b8', lineHeight: 18 },
  centerContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 40, marginTop: 40 },
  emptyIconCircle: { width: 80, height: 80, borderRadius: 40, backgroundColor: '#f1f5f9', alignItems: 'center', justifyContent: 'center', marginBottom: 16 },
  emptyTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginBottom: 8 },
  emptyText: { fontSize: 15, color: '#64748b', textAlign: 'center', lineHeight: 22 },
});